import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { billingApi } from '@/api';
import { useKeyboardShortcuts } from '@/core/keyboard';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import type { HeldBillResponse } from '@/types/billing';
import { deserializeCart } from '@/utils/posCart';
import { formatDateTime } from '@/utils/format';
import { showApiError } from '@/utils/apiError';
import { PageHeader } from '@/components/layout/PageHeader';
import { notify } from '@/core/toast/notify';

function HeldBillsContent() {
  const store = useAppSelector((s) => s.app.selectedStore);
  const navigate = useNavigate();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const [bills, setBills] = useState<HeldBillResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const load = useCallback(async () => {
    if (!store) return;
    setLoading(true);
    try {
      const list = await billingApi.listHeldBills(store.id);
      setBills(list);
      setSelectedIndex(0);
    } catch (err) {
      showApiError(err, 'Failed to load held bills');
    } finally {
      setLoading(false);
    }
  }, [store]);

  useEffect(() => {
    load();
  }, [load]);

  const resume = useCallback(
    async (bill: HeldBillResponse) => {
      try {
        const restored = await billingApi.restoreHeldBill(bill.id);
        const cart = deserializeCart(restored.billData);
        if (!cart) {
          notify.error('Invalid held bill data');
          return;
        }
        if (restored.customerId && !cart.customerId) {
          cart.customerId = restored.customerId;
        }
        sessionStorage.setItem('saaspos_resume_cart', JSON.stringify(cart));
        sessionStorage.setItem('saaspos_resume_held_bill_id', String(restored.heldBillId));
        notify.success('Bill loaded — complete at billing');
        navigate('/pos');
      } catch (err) {
        showApiError(err, 'Resume failed');
      }
    },
    [navigate],
  );

  const cancelBill = useCallback(
    async (bill: HeldBillResponse) => {
      const ok = await confirm({
        title: 'Cancel held bill?',
        message: `Release stock reservation for ${bill.holdReference ?? `Hold #${bill.id}`}? This cannot be undone.`,
        confirmLabel: 'Cancel bill',
        variant: 'danger',
      });
      if (!ok) return;
      try {
        await billingApi.cancelHeldBill(bill.id);
        notify.success('Held bill cancelled');
        await load();
      } catch (err) {
        showApiError(err, 'Cancel failed');
      }
    },
    [confirm, load],
  );

  useKeyboardShortcuts(
    [
      {
        id: 'held-esc',
        keys: ['escape'],
        label: 'Esc',
        description: 'Back to billing',
        category: 'Navigation',
        handler: () => navigate('/pos'),
      },
      {
        id: 'held-enter',
        keys: ['enter'],
        label: 'Enter',
        description: 'Resume bill',
        category: 'Hold Bills',
        enabled: () => bills.length > 0,
        handler: () => {
          const bill = bills[selectedIndex];
          if (bill) void resume(bill);
        },
      },
      {
        id: 'held-f1-billing',
        keys: ['f1'],
        label: 'F1',
        description: 'Billing',
        category: 'Navigation',
        handler: () => navigate('/pos'),
      },
    ],
    [bills, selectedIndex, resume, navigate],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, Math.max(0, bills.length - 1)));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [bills.length]);

  if (!store) {
    return (
      <div className="flex h-full min-h-0 items-center justify-center overflow-y-auto p-8 text-slate-500">
        No store selected.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-slate-200 bg-white px-6 py-4 dark:border-slate-700 dark:bg-slate-900">
        <PageHeader
          className="mb-0"
          title={`Held bills — ${store.name}`}
          description="↑↓ navigate · Enter resume · Esc back to billing"
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6 pb-8">
        {loading ? (
          <p className="text-slate-500">Loading…</p>
        ) : bills.length === 0 ? (
          <p className="text-slate-500">No held bills. Press Esc to return to billing.</p>
        ) : (
          <ul className="space-y-2" role="listbox">
            {bills.map((bill, i) => (
              <li key={bill.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === selectedIndex}
                  onClick={() => void resume(bill)}
                  onMouseEnter={() => setSelectedIndex(i)}
                  className={`flex w-full items-center justify-between rounded-lg border-2 px-4 py-3 text-left transition ${
                    i === selectedIndex
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/30'
                      : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800'
                  }`}
                >
                  <div>
                    <p className="font-semibold text-slate-900 dark:text-white">
                      {bill.holdReference ?? `Hold #${bill.id}`}
                    </p>
                    <p className="text-sm text-slate-500">{formatDateTime(bill.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-primary-600">Enter → Resume</span>
                    <button
                      type="button"
                      className="text-xs text-red-600 hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        void cancelBill(bill);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <footer className="border-t border-slate-300 bg-slate-800 px-4 py-2 text-xs text-slate-300">
        Enter open · Esc billing
      </footer>
      {confirmDialog}
    </div>
  );
}

export function HeldBillsPage() {
  return <HeldBillsContent />;
}
